import { matchSuppliers, type SupplierCandidate } from "@bl/domain";
import { Trash2 } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { type CatalogProduct, type CatalogSupplier, type Supplier, useCatalog } from "../api";
import { CategoryChip } from "../components/ui";

/**
 * The parts of entering an invoice by hand that look things up as they are typed: the supplier, and
 * each line against the catalog (Tyler, 2026-09-28).
 */

/* ---------------------------------------------------------------- the supplier */

/** The select's value for a supplier typed rather than chosen. */
export const OTHER_SUPPLIER = "__other__";

/** A supplier chosen from the list, or typed: `other` is null unless "Other…" is picked. */
export interface SupplierChoice {
  supplierId: string;
  other: string | null;
}

export const noSupplier: SupplierChoice = { supplierId: "", other: null };

export interface SupplierOnRecord extends SupplierCandidate {
  /** On file here, or known only to the catalog and added when the review starts. */
  kind: "ours" | "catalog";
  /** The catalog company it is, where there is one: what says whose SKUs these are. */
  companyId: string | null;
}

/**
 * Everything a typed supplier is compared against: this brewery's own first, carrying the other
 * names of the catalog company each is linked to, then the catalog's sellers nobody here is linked
 * to yet.
 */
export function suppliersOnRecord(
  ours: readonly Supplier[],
  sellers: readonly CatalogSupplier[],
): SupplierOnRecord[] {
  const sellerById = new Map(sellers.map((s) => [s.id, s]));
  const linked = new Set(ours.map((s) => s.catalogCompanyId).filter(Boolean));
  return [
    ...ours.map((s) => ({
      id: s.id,
      name: s.name,
      aliases: (s.catalogCompanyId && sellerById.get(s.catalogCompanyId)?.aliases) || [],
      kind: "ours" as const,
      companyId: s.catalogCompanyId,
    })),
    ...sellers
      .filter((s) => !linked.has(s.id))
      .map((s) => ({
        id: s.id,
        name: s.name,
        aliases: s.aliases,
        kind: "catalog" as const,
        companyId: s.id,
      })),
  ];
}

export interface ResolvedSupplier {
  /** Nothing chosen or typed. */
  said: boolean;
  /** One on file. */
  supplierId?: string;
  /** The name to file it under; a new supplier is made from it when there is no id. */
  supplierName?: string;
  /** The catalog company, where known, so a SKU can be read as that seller's. */
  companyId: string | null;
  /** What was typed turned out to be one on record under another spelling. */
  sameAs?: SupplierOnRecord;
}

/**
 * What the choice comes to. A typed name that is one on record by any name it goes by is that one,
 * never a second: "country malt group, inc." is the Country Malt Group already on file, and "BSG" is
 * the catalog's Brewers Supply Group.
 */
export function resolveSupplier(
  choice: SupplierChoice,
  onRecord: readonly SupplierOnRecord[],
): ResolvedSupplier {
  if (choice.other === null) {
    const chosen = onRecord.find((s) => s.kind === "ours" && s.id === choice.supplierId);
    return chosen
      ? {
          said: true,
          supplierId: chosen.id,
          supplierName: chosen.name,
          companyId: chosen.companyId,
        }
      : { said: false, companyId: null };
  }
  const typed = choice.other.trim();
  if (!typed) return { said: false, companyId: null };
  const [best] = matchSuppliers(typed, onRecord, 1);
  if (best?.exact) {
    const same = best.candidate;
    return {
      said: true,
      ...(same.kind === "ours" ? { supplierId: same.id } : {}),
      supplierName: same.name,
      companyId: same.companyId,
      sameAs: same,
    };
  }
  return { said: true, supplierName: typed, companyId: null };
}

/**
 * The supplier select, with "Other…" at the end for one not on it. Typing there suggests the ones on
 * record the name could mean, and says plainly whether the invoice will go under one of them or add
 * a new supplier.
 */
export function SupplierField({
  id,
  ours,
  onRecord,
  choice,
  onChange,
}: {
  id?: string;
  ours: readonly Supplier[];
  onRecord: readonly SupplierOnRecord[];
  choice: SupplierChoice;
  onChange: (choice: SupplierChoice) => void;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const typing = choice.other !== null;
  const matches = typing ? matchSuppliers(choice.other ?? "", onRecord) : [];
  const resolved = resolveSupplier(choice, onRecord);
  const pick = (s: SupplierOnRecord) => {
    onChange(
      s.kind === "ours" ? { supplierId: s.id, other: null } : { supplierId: "", other: s.name },
    );
    setOpen(false);
  };
  const keys = useSuggestionKeys(matches.length, open, setOpen, (i) => {
    const hit = matches[i];
    if (hit) pick(hit.candidate);
  });
  const shown = open && matches.length > 0;
  return (
    <div className="supplier-field">
      <select
        id={id}
        className="select"
        value={typing ? OTHER_SUPPLIER : choice.supplierId}
        onChange={(e) =>
          onChange(
            e.target.value === OTHER_SUPPLIER
              ? { supplierId: "", other: "" }
              : { supplierId: e.target.value, other: null },
          )
        }
      >
        <option value="">Select supplier</option>
        {ours.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
        <option value={OTHER_SUPPLIER}>Other…</option>
      </select>
      {typing ? (
        <div className="suggest-anchor">
          <input
            className="input"
            placeholder="Supplier's name"
            aria-label="Supplier's name"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={shown}
            aria-controls={listId}
            aria-activedescendant={
              shown && keys.active >= 0 ? `${listId}-${keys.active}` : undefined
            }
            // biome-ignore lint/a11y/noAutofocus: choosing "Other…" is asking to type one
            autoFocus
            value={choice.other ?? ""}
            onChange={(e) => {
              onChange({ supplierId: "", other: e.target.value });
              keys.reset();
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={keys.onKeyDown}
          />
          {shown ? (
            <div
              className="suggest-pop"
              id={listId}
              role="listbox"
              aria-label="Suppliers on record"
            >
              {matches.map(({ candidate }, i) => (
                <button
                  key={`${candidate.kind}-${candidate.id}`}
                  id={`${listId}-${i}`}
                  type="button"
                  role="option"
                  aria-selected={i === keys.active}
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(candidate)}
                >
                  <span className="suggestion-main">
                    <strong>{candidate.name}</strong>
                    <span className={`chip plain ${candidate.kind === "ours" ? "ok" : ""}`}>
                      {candidate.kind === "ours" ? "On file" : "In the catalog"}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {/* A supplier recognised says so in the list ("On file", "In the catalog"), so the note is
              only for one that is not, and waits while the list is offering ones that are. */}
          {shown || !resolved.said || resolved.sameAs ? null : (
            <span className="hint">
              Not on record yet. It is added to your suppliers when the review starts.
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- the lines */

export interface LineDraft {
  key: string;
  supplierSku: string;
  description: string;
  billedUnits: string;
  unitPrice: string;
  extendedPrice: string;
  /** Picked from the catalog and already stocked here: the line goes straight to that item. */
  itemId?: string;
  pickedName?: string;
}

export const blankLine = (): LineDraft => ({
  key: crypto.randomUUID?.() ?? String(Math.random()),
  supplierSku: "",
  description: "",
  billedUnits: "",
  unitPrice: "",
  extendedPrice: "",
});

/** The SKU a catalog product would fill in, and whose it is. */
export interface OfferedSku {
  sku: string;
  supplier: { id: string; name: string };
}

/**
 * One line of the invoice. Typing two letters or more into the SKU or the description offers what
 * the catalog has by that name, maker or SKU, the way adding an item does; picking one fills the line
 * in. Nothing is chosen for the brewer: the fields stay theirs to change, and changing them lets go
 * of the pick. What the invoice's supplier sells comes first.
 *
 * The list hangs off the description whichever of the two is being typed in, so on a phone, where
 * the line stacks, it opens under the field rather than under the whole line.
 */
export function LineEntry({
  line,
  onEdit,
  onPick,
  skuFor,
  sellsIt,
  onRemove,
  canRemove,
}: {
  line: LineDraft;
  onEdit: (patch: Partial<LineDraft>) => void;
  /** `typedSku` is what was in the SKU box, when that is where the search came from. */
  onPick: (product: CatalogProduct, typedSku: string | null) => void;
  skuFor: (product: CatalogProduct, typedSku: string | null) => OfferedSku | null;
  /** Whether the invoice's supplier sells it, where the catalog knows who that is. */
  sellsIt: ((product: CatalogProduct) => boolean) | null;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const listId = useId();
  const [field, setField] = useState<"sku" | "description">("description");
  const [open, setOpen] = useState(false);
  const typed = field === "sku" ? line.supplierSku : line.description;
  const [query, setQuery] = useState(typed);
  useEffect(() => {
    const t = setTimeout(() => setQuery(typed), 200);
    return () => clearTimeout(t);
  }, [typed]);
  const searching = open && query.trim().length >= 2;
  const { data = [] } = useCatalog(searching ? query : "", null);
  const products = searching
    ? (sellsIt ? [...data].sort((a, b) => Number(sellsIt(b)) - Number(sellsIt(a))) : data).slice(
        0,
        6,
      )
    : [];
  const typedSku = field === "sku" ? line.supplierSku : null;
  const pick = (product: CatalogProduct) => {
    onPick(product, typedSku);
    setOpen(false);
  };
  const keys = useSuggestionKeys(products.length, open, setOpen, (i) => {
    const product = products[i];
    if (product) pick(product);
  });
  const shown = products.length > 0;
  const listRef = useRef<HTMLDivElement>(null);
  // A phone's keyboard leaves little room; bring the list into view when it opens.
  useEffect(() => {
    if (shown) listRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [shown]);
  const combobox = (which: "sku" | "description") => ({
    role: "combobox" as const,
    "aria-autocomplete": "list" as const,
    "aria-expanded": shown && field === which,
    "aria-controls": listId,
    "aria-activedescendant":
      shown && field === which && keys.active >= 0 ? `${listId}-${keys.active}` : undefined,
    onFocus: () => setField(which),
    onBlur: () => setOpen(false),
    onKeyDown: keys.onKeyDown,
  });
  const typedInto = (which: "sku" | "description", patch: Partial<LineDraft>) => {
    setField(which);
    onEdit(patch);
    keys.reset();
    setOpen(true);
  };

  return (
    <div className="entry-line">
      <div className="entry-row">
        <input
          className="input"
          placeholder="SKU"
          aria-label="SKU"
          value={line.supplierSku}
          onChange={(e) => typedInto("sku", { supplierSku: e.target.value })}
          {...combobox("sku")}
        />
        <div className="suggest-anchor entry-description">
          <input
            className="input"
            placeholder="Description, as printed"
            aria-label="Description"
            value={line.description}
            onChange={(e) => typedInto("description", { description: e.target.value })}
            {...combobox("description")}
          />
          {shown ? (
            <div
              ref={listRef}
              className="suggest-pop"
              id={listId}
              role="listbox"
              aria-label="From the catalog"
            >
              {products.map((p, i) => {
                const offered = skuFor(p, typedSku);
                return (
                  <button
                    key={p.id}
                    id={`${listId}-${i}`}
                    type="button"
                    role="option"
                    aria-selected={i === keys.active}
                    tabIndex={-1}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(p)}
                  >
                    <span className="suggestion-main">
                      <strong>{p.name}</strong>
                      <CategoryChip category={p.category} />
                      {p.adoptedItemIds.length ? (
                        <span className="chip ok plain">In inventory</span>
                      ) : null}
                    </span>
                    <span className="muted">
                      {[p.manufacturer, offered ? `${offered.supplier.name} ${offered.sku}` : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
        <input
          className="input num"
          placeholder="Qty"
          aria-label="Quantity"
          inputMode="decimal"
          value={line.billedUnits}
          onChange={(e) => onEdit({ billedUnits: e.target.value })}
        />
        <input
          className="input num"
          placeholder="Unit price"
          aria-label="Unit price"
          inputMode="decimal"
          value={line.unitPrice}
          onChange={(e) => onEdit({ unitPrice: e.target.value })}
        />
        <input
          className="input num"
          placeholder="Total"
          aria-label="Line total"
          inputMode="decimal"
          value={line.extendedPrice}
          onChange={(e) => onEdit({ extendedPrice: e.target.value })}
        />
        <button
          type="button"
          className="icon-button"
          aria-label="Remove this line"
          disabled={!canRemove}
          onClick={onRemove}
        >
          <Trash2 size={16} />
        </button>
      </div>
      {line.itemId ? (
        <p className="entry-note">
          <span className="chip ok plain">In inventory</span> {line.pickedName}: this line goes
          straight to it.
        </p>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- keys */

/**
 * The keys of a list offered under a field: arrows move through it, Enter takes the one marked, and
 * Escape puts the list away without closing the dialog it sits in. Focus stays in the field.
 */
function useSuggestionKeys(
  count: number,
  open: boolean,
  setOpen: (open: boolean) => void,
  onPick: (index: number) => void,
) {
  const [active, setActive] = useState(-1);
  const marked = open && active < count ? active : -1;
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && count > 0) {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1 >= count ? 0 : a + 1));
    } else if (e.key === "ArrowUp" && count > 0) {
      e.preventDefault();
      setActive((a) => (a <= 0 ? count - 1 : a - 1));
    } else if (e.key === "Enter" && marked >= 0) {
      e.preventDefault();
      onPick(marked);
    } else if (e.key === "Escape" && open && count > 0) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };
  return { active: marked, onKeyDown, reset: () => setActive(-1) };
}
